import gql from "graphql-tag";

const conciergeBookingFields = gql`
  fragment ConciergeBookingFields on Bookings {
    id
    plate
    guestName
    guestEmail
    guestPhone
    guestRoom
    guestApartmentNumber
    guestReservation
    guestCompany
    guestDepartment
    guestEmployeeCode
    guestPosition
    carType
    carNotes
    startDay
    endDay
    expectedDepartureAt
    expectedArrivalAt
    arrivalMode
    status
    stripeSource
    stripeCustomerId
    payLater
    rateCardId
    isVip
    vipAlertedAt

    vipContacts {
      contactId
      alertedAt
    }

    rateCard {
      name
      version
      type
      isVip
      isValet
      entryAfterTime
      exitBeforeTime
      paymentStripe
      valetPayLater
      valetRequestFeeAud

      lot {
        id
        name
        type
      }
    }

    paymentState {
      isPaid
      amountAud
      paidAt
      lastChargeStatus
    }

    offsiteTickets {
      id
      ticketCode
      status
    }
  }
`;

/*
 * A runner holds runner_queue:read and nothing else, so the server gives them a
 * narrow projection of the booking rather than the full type - no guest contact
 * details, no payment identifiers. Everything here is something you need in your
 * hand to fetch a car.
 */
const runnerBookingFields = gql`
  fragment RunnerBookingFields on ValetRunnerBooking {
    id
    plate
    guestName
    carType
    carNotes
    status
    expectedDepartureAt
    isVip
    paymentRequired
    paid

    lotId
    lotName
    lotType
  }
`;

/* The session half, identical for the desk and the runner. */
const valetSessionCoreFields = gql`
  fragment ValetSessionCoreFields on ValetSession {
    id
    plate
    status
    arrivedAt
    entryGateDescription
    notes
    claimedAt
    parkedByUserId
    parkedAt
    parkedLocation
    requestedAt
    pickedByUserId
    returningAt
    enRouteAt
    locatedAt
    enRouteToGuestAt
    readyAt
    returnedAt
    pickupCommittedAt
    lotId
    bay
    offsiteTicket

    # Where the car actually is, which is not always where its guest card
    # pointed it. Narrow projection: a name, not the whole Lot type.
    lot {
      id
      name
      type
    }

    parkedByUser {
      id
      name
    }

    pickedByUser {
      id
      name
    }
  }
`;

/*
 * Shared session shape. The mutations below return it too, so Apollo can patch
 * the queue row in place by id instead of waiting on the next poll.
 */
const conciergeValetSessionFields = gql`
  fragment ConciergeValetSessionFields on ValetSession {
    ...ValetSessionCoreFields

    booking {
      ...ConciergeBookingFields
    }

    # The installation's plate whitelist, matched server-side on the OCR-folded
    # plate. Null unless this car is on it - the one legitimate reason for a car
    # on the forecourt to have no booking.
    sanctionedPlate {
      id
      plate
      label
      isVip
      lotName
    }
  }
  ${valetSessionCoreFields}
  ${conciergeBookingFields}
`;

/*
 * Live lot occupancy for the concierge dashboard. The server owns the meaning
 * of "current" for the occupancy count. Modal rows are reused from the
 * dashboard's existing valetOnSiteSessions query to avoid downloading the same
 * session projection twice.
 */
export const conciergeParkedByLot = gql`
  query conciergeParkedByLot($installationId: ID!) {
    allLots(
      filter: { installationId: $installationId }
      orderBy: name_ASC
    ) {
      id
      name
      type
      capacity
      currentValetSessionCount
    }
  }
`;

/* Same rows, seen from the runner queue: the narrow booking, never the full one. */
const runnerValetSessionFields = gql`
  fragment RunnerValetSessionFields on ValetSession {
    ...ValetSessionCoreFields

    runnerBooking {
      ...RunnerBookingFields
    }
  }
  ${valetSessionCoreFields}
  ${runnerBookingFields}
`;

/*
 * Arrivals queue: cars waiting on the forecourt for a concierge.
 *
 * DETECTED only - a claimed car is someone's job now, not a queue entry, so it
 * drops off every tablet as soon as its intake finishes. Without this filter
 * the server would return the whole active set (CLAIMED/PARKED/REQUESTED too).
 */
export const valetArrivalsQueue = gql`
  query valetArrivalsQueue($facilityId: ID, $limit: Int) {
    valetSessions(
      facilityId: $facilityId
      limit: $limit
      statuses: [DETECTED]
    ) {
      items {
        ...ConciergeValetSessionFields
      }
      count
    }
  }
  ${conciergeValetSessionFields}
`;

/*
 * The runner queue, both directions of it: cars waiting to be parked
 * (CLAIMED/PARKING) and cars the guest has called up (REQUESTED/RETURNING).
 *
 * One query rather than two - the runner card, the runner app and the
 * dashboard's Requested tab all read the same rows, so splitting it would only
 * buy extra round trips.
 */
export const valetParkingQueue = gql`
  query valetParkingQueue($facilityId: ID, $limit: Int) {
    valetSessions(
      facilityId: $facilityId
      limit: $limit
      statuses: [
        CLAIMED
        PARKING
        REQUESTED
        RUNNER_ASSIGNED
        EN_ROUTE
        VEHICLE_LOCATED
        RETURNING
        READY
      ]
    ) {
      items {
        ...ConciergeValetSessionFields
      }
      count
    }
  }
  ${conciergeValetSessionFields}
`;

/*
 * On lot: cars a runner has parked and that have not been called for yet.
 */
/*
 * Every car the valet still holds, for the board's On site column. One query
 * rather than one per tab: the tabs are a split of the same set, and four
 * polls of the same rows would only disagree with each other.
 */
export const valetOnSiteSessions = gql`
  query valetOnSiteSessions($facilityId: ID, $limit: Int) {
    valetSessions(
      facilityId: $facilityId
      limit: $limit
      statuses: [
        CLAIMED
        PARKING
        PARKED
        REQUESTED
        RUNNER_ASSIGNED
        EN_ROUTE
        VEHICLE_LOCATED
        RETURNING
        READY
      ]
    ) {
      items {
        ...ConciergeValetSessionFields
      }
      count
    }
  }
  ${conciergeValetSessionFields}
`;

export const valetParkedSessions = gql`
  query valetParkedSessions($facilityId: ID, $limit: Int) {
    valetSessions(facilityId: $facilityId, limit: $limit, statuses: [PARKED, REQUESTED]) {
      items {
        ...ConciergeValetSessionFields
      }
      count
    }
  }
  ${conciergeValetSessionFields}
`;


export const valetRunnerParkingQueue = gql`
  query valetRunnerParkingQueue($facilityId: ID, $limit: Int) {
    valetRunnerSessions(
      facilityId: $facilityId
      limit: $limit
      statuses: [
        CLAIMED
        PARKING
        REQUESTED
        RUNNER_ASSIGNED
        EN_ROUTE
        VEHICLE_LOCATED
        RETURNING
        READY
      ]
    ) {
      items {
        ...RunnerValetSessionFields
      }
      count
    }
  }
  ${runnerValetSessionFields}
`;

export const valetRunnerParkedSessions = gql`
  query valetRunnerParkedSessions($facilityId: ID, $limit: Int) {
    valetRunnerSessions(
      facilityId: $facilityId
      limit: $limit
      statuses: [PARKED, REQUESTED]
    ) {
      items {
        ...RunnerValetSessionFields
      }
      count
    }
  }
  ${runnerValetSessionFields}
`;

/*
 * Plate lookup: the desk asking "where is this car?".
 *
 * Server-side search, not a filter over the lot list - the lot is longer than a
 * page, and the concierge is asking about a specific car, so the match has to be
 * made where all the rows are. Same statuses as the lot: a car that has already
 * been called up must still be findable.
 */
export const valetLotSearch = gql`
  query valetLotSearch($facilityId: ID, $search: String, $limit: Int) {
    valetSessions(
      facilityId: $facilityId
      search: $search
      limit: $limit
      statuses: [PARKED, REQUESTED]
    ) {
      items {
        ...ConciergeValetSessionFields
      }
      count
    }
  }
  ${conciergeValetSessionFields}
`;

/*
 * Expected Arrivals: bookings that have not been entered yet
 */
export const expectedArrivals = gql`
  query expectedArrivals(
    $offset: Int!
    $limit: Int
    $startDayFrom: String
    $search: String
    $isValet: Boolean
  ) {
    listBookings(
      offset: $offset
      limit: $limit
      status: "PENDING_ENTRY"
      startDayFrom: $startDayFrom
      search: $search
      isValet: $isValet
      orderBy: expectedArrival_ASC
    ) {
      items {
        ...ConciergeBookingFields
      }
      count
    }
  }
  ${conciergeBookingFields}
`;

export const valetIntakeCandidates = gql`
  query valetIntakeCandidates(
    $offset: Int!
    $limit: Int
    $search: String
    $isValet: Boolean
  ) {
    listBookings(
      offset: $offset
      limit: $limit
      statuses: ["PENDING_ENTRY", "CURRENT"]
      search: $search
      isValet: $isValet
      orderBy: expectedArrival_ASC
    ) {
      items {
        ...ConciergeBookingFields
      }
      count
    }
  }
  ${conciergeBookingFields}
`;

export const createConciergeBooking = gql`
  mutation createConciergeBooking(
    $installationId: ID!
    $rateCardId: ID!
    $plate: String!
    $entryEventId: ID
    $startDay: String
    $endDay: String
    $expectedDepartureAt: String
    $expectedArrivalAt: String
    $arrivalMode: ArrivalModeEnum
    $stripeCustomerId: String
    $payLater: Boolean
    $isVip: Boolean
    $vipContactIds: [ID!]

    $guestName: String
    $guestEmail: String
    $guestCompany: String
    $guestDepartment: String
    $guestPhone: String
    $guestRoom: String
    $guestApartmentNumber: String
    $guestReservation: String
    $guestEmployeeCode: String
    $guestPosition: String
    $carType: String
    $carNotes: String

    $valetSessionId: ID
  ) {
    createBooking(
      booking: {
        installationId: $installationId
        rateCardId: $rateCardId
        plate: $plate
        entryEventId: $entryEventId
        valetSessionId: $valetSessionId
        startDay: $startDay
        endDay: $endDay
        expectedDepartureAt: $expectedDepartureAt
        expectedArrivalAt: $expectedArrivalAt
        arrivalMode: $arrivalMode
        stripeCustomerId: $stripeCustomerId
        payLater: $payLater
        isVip: $isVip
        vipContactIds: $vipContactIds

        guestName: $guestName
        guestEmail: $guestEmail
        guestCompany: $guestCompany
        guestDepartment: $guestDepartment
        guestPhone: $guestPhone
        guestRoom: $guestRoom
        guestApartmentNumber: $guestApartmentNumber
        guestReservation: $guestReservation
        guestEmployeeCode: $guestEmployeeCode
        guestPosition: $guestPosition
        carType: $carType
        carNotes: $carNotes
      }
    ) {
      ...ConciergeBookingFields

      valetSession {
        id
        status
      }
    }
  }
  ${conciergeBookingFields}
`;

/*
 * Full booking edit. Every field updateBooking rewrites has to be sent back or
 * the server blanks it - status and stripeCustomerId included.
 */
export const updateConciergeBooking = gql`
  mutation updateConciergeBooking(
    $installationId: ID!
    $bookingId: ID!
    $rateCardId: ID!
    $plate: String!
    $startDay: String
    $endDay: String
    $expectedDepartureAt: String
    $expectedArrivalAt: String
    $arrivalMode: ArrivalModeEnum
    $stripeCustomerId: String
    $payLater: Boolean
    $status: BookingStatusEnum
    $isVip: Boolean
    $vipContactIds: [ID!]

    $guestName: String
    $guestEmail: String
    $guestCompany: String
    $guestDepartment: String
    $guestPhone: String
    $guestRoom: String
    $guestApartmentNumber: String
    $guestReservation: String
    $guestEmployeeCode: String
    $guestPosition: String
    $carType: String
    $carNotes: String
  ) {
    updateBooking(
      booking: {
        installationId: $installationId
        id: $bookingId
        rateCardId: $rateCardId
        plate: $plate
        startDay: $startDay
        endDay: $endDay
        expectedDepartureAt: $expectedDepartureAt
        expectedArrivalAt: $expectedArrivalAt
        arrivalMode: $arrivalMode
        stripeCustomerId: $stripeCustomerId
        payLater: $payLater
        status: $status
        isVip: $isVip
        vipContactIds: $vipContactIds

        guestName: $guestName
        guestEmail: $guestEmail
        guestCompany: $guestCompany
        guestDepartment: $guestDepartment
        guestPhone: $guestPhone
        guestRoom: $guestRoom
        guestApartmentNumber: $guestApartmentNumber
        guestReservation: $guestReservation
        guestEmployeeCode: $guestEmployeeCode
        guestPosition: $guestPosition
        carType: $carType
        carNotes: $carNotes
      }
    ) {
      ...ConciergeBookingFields
    }
  }
  ${conciergeBookingFields}
`;

/*
 * Opens a valet session for a freshly created booking, or links the booking to
 * the session the car already has. Safe to call twice - the server is
 * idempotent on (facility, plate).
 */
export const ensureValetSessionForBooking = gql`
  mutation ensureValetSessionForBooking(
    $bookingId: ID!
    $facilityId: ID!
    $valetSessionId: ID
  ) {
    ensureValetSessionForBooking(
      bookingId: $bookingId
      facilityId: $facilityId
      valetSessionId: $valetSessionId
    ) {
      ...ConciergeValetSessionFields
    }
  }
  ${conciergeValetSessionFields}
`;

/*
 * Keys accepted: claims the session and starts the booking
 */
export const claimValetSession = gql`
  mutation claimValetSession($valetSessionId: ID!, $facilityId: ID!) {
    claimValetSession(
      valetSessionId: $valetSessionId
      facilityId: $facilityId
    ) {
      valetSession {
        ...ConciergeValetSessionFields
      }
      bookingTransitioned
      bookingStatus
    }
  }
  ${conciergeValetSessionFields}
`;

export const dismissValetSession = gql`
  mutation dismissValetSession(
    $valetSessionId: ID!
    $facilityId: ID!
    $reason: String
  ) {
    dismissValetSession(
      valetSessionId: $valetSessionId
      facilityId: $facilityId
      reason: $reason
    ) {
      ...ConciergeValetSessionFields
    }
  }
  ${conciergeValetSessionFields}
`;

/*
 * Runner lifecycle. Each is a compare-and-set server-side, so a car another
 * runner already took comes back as VALET_SESSION_STATUS_CONFLICT rather than
 * being handed to two people.
 */
export const startValetParking = gql`
  mutation startValetParking($valetSessionId: ID!, $facilityId: ID!) {
    startValetParking(
      valetSessionId: $valetSessionId
      facilityId: $facilityId
    ) {
      ...ConciergeValetSessionFields
    }
  }
  ${conciergeValetSessionFields}
`;

export const returnValetSessionToQueue = gql`
  mutation returnValetSessionToQueue($valetSessionId: ID!, $facilityId: ID!) {
    returnValetSessionToQueue(
      valetSessionId: $valetSessionId
      facilityId: $facilityId
    ) {
      ...ConciergeValetSessionFields
    }
  }
  ${conciergeValetSessionFields}
`;

/* PARKED leaves the runner queue's status filter, so the list has to be
 * refetched - the cached row would otherwise linger with a stale status. */
export const confirmValetSessionParked = gql`
  mutation confirmValetSessionParked(
    $valetSessionId: ID!
    $facilityId: ID!
    $lotId: ID
    $bay: String
    $offsiteTicket: String
  ) {
    confirmValetSessionParked(
      valetSessionId: $valetSessionId
      facilityId: $facilityId
      lotId: $lotId
      bay: $bay
      offsiteTicket: $offsiteTicket
    ) {
      ...ConciergeValetSessionFields
    }
  }
  ${conciergeValetSessionFields}
`;

/*
 * The desk books a car for a time. It goes into the runner queue straight
 * away, due at that time; a pickup already queued is moved to it.
 */
export const scheduleValetSessionReturn = gql`
  mutation scheduleValetSessionReturn(
    $valetSessionId: ID!
    $facilityId: ID!
    $requestedAt: DateTime!
  ) {
    scheduleValetSessionReturn(
      valetSessionId: $valetSessionId
      facilityId: $facilityId
      requestedAt: $requestedAt
    ) {
      ...ConciergeValetSessionFields
    }
  }
  ${conciergeValetSessionFields}
`;

/* Calls a pickup off before a runner has the keys; the car goes back to parked. */
export const cancelValetSessionReturn = gql`
  mutation cancelValetSessionReturn($valetSessionId: ID!, $facilityId: ID!) {
    cancelValetSessionReturn(
      valetSessionId: $valetSessionId
      facilityId: $facilityId
    ) {
      ...ConciergeValetSessionFields
    }
  }
  ${conciergeValetSessionFields}
`;

export const requestValetSessionReturn = gql`
  mutation requestValetSessionReturn($valetSessionId: ID!, $facilityId: ID!) {
    requestValetSessionReturn(
      valetSessionId: $valetSessionId
      facilityId: $facilityId
    ) {
      ...ConciergeValetSessionFields
    }
  }
  ${conciergeValetSessionFields}
`;

export const startValetReturn = gql`
  mutation startValetReturn($valetSessionId: ID!, $facilityId: ID!) {
    startValetReturn(
      valetSessionId: $valetSessionId
      facilityId: $facilityId
    ) {
      ...ConciergeValetSessionFields
    }
  }
  ${conciergeValetSessionFields}
`;

/*
 * A runner scans the guest's walk-up QR: the call-up and the accept happen in
 * one write, and an outstanding balance does not stop it - the runner settles it
 * from the active-job card before handing the keys over.
 */
export const requestAndStartValetReturn = gql`
  mutation requestAndStartValetReturn($bookingId: ID!, $facilityId: ID!) {
    requestAndStartValetReturn(
      bookingId: $bookingId
      facilityId: $facilityId
    ) {
      ...RunnerValetSessionFields
    }
  }
  ${runnerValetSessionFields}
`;

/*
 * The steps between taking a pickup and handing it over. Each is one tap in the
 * runner queue, and each is refused server-side unless the step before it has
 * happened - a runner who loses signal cannot skip one on the way back.
 */
/*
 * The booking behind one row, for a reader the queue's narrow projection does
 * not serve. The runner queue deliberately never asks for this in its list
 * query - it polls, and a runner may not read it - so a concierge working that
 * queue fills it in one row at a time, when they open one.
 */
export const conciergeBookingView = gql`
  query conciergeBookingView($bookingId: ID!) {
    booking(id: $bookingId) {
      ...ConciergeBookingFields
    }
  }
  ${conciergeBookingFields}
`;

/*
 * The long tail of a booking, fetched only when the desk opens the booking
 * view's More section: every valet session this booking has had, and the
 * emails it triggered. Both are desk-only - the runner projection carries
 * neither.
 */
export const conciergeBookingDetail = gql`
  query conciergeBookingDetail($bookingId: ID!) {
    booking(id: $bookingId) {
      id
      emails {
        id
        createdAt
        templateClass
        finalisedAt
      }
      valetSessions {
        id
        status
        arrivedAt
        claimedAt
        parkedAt
        parkedLocation
        bay
        requestedAt
        returnedAt
      }
    }
  }
`;

/*
 * The lots a runner may say they parked in: name and kind only, behind
 * runner_queue:read rather than the parking_lots:read the settings pages use.
 */
export const valetRunnerLots = gql`
  query valetRunnerLots($facilityId: ID) {
    valetRunnerLots(facilityId: $facilityId) {
      id
      name
      type
    }
  }
`;

export const startValetEnRoute = gql`
  mutation startValetEnRoute($valetSessionId: ID!, $facilityId: ID!) {
    startValetEnRoute(valetSessionId: $valetSessionId, facilityId: $facilityId) {
      ...ConciergeValetSessionFields
    }
  }
  ${conciergeValetSessionFields}
`;

export const markValetVehicleLocated = gql`
  mutation markValetVehicleLocated($valetSessionId: ID!, $facilityId: ID!) {
    markValetVehicleLocated(
      valetSessionId: $valetSessionId
      facilityId: $facilityId
    ) {
      ...ConciergeValetSessionFields
    }
  }
  ${conciergeValetSessionFields}
`;

export const markValetCollected = gql`
  mutation markValetCollected($valetSessionId: ID!, $facilityId: ID!) {
    markValetCollected(valetSessionId: $valetSessionId, facilityId: $facilityId) {
      ...ConciergeValetSessionFields
    }
  }
  ${conciergeValetSessionFields}
`;

export const markValetReady = gql`
  mutation markValetReady($valetSessionId: ID!, $facilityId: ID!) {
    markValetReady(valetSessionId: $valetSessionId, facilityId: $facilityId) {
      ...ConciergeValetSessionFields
    }
  }
  ${conciergeValetSessionFields}
`;

export const returnValetPickupToQueue = gql`
  mutation returnValetPickupToQueue($valetSessionId: ID!, $facilityId: ID!) {
    returnValetPickupToQueue(
      valetSessionId: $valetSessionId
      facilityId: $facilityId
    ) {
      ...ConciergeValetSessionFields
    }
  }
  ${conciergeValetSessionFields}
`;

export const confirmValetSessionReturned = gql`
  mutation confirmValetSessionReturned($valetSessionId: ID!, $facilityId: ID!) {
    confirmValetSessionReturned(
      valetSessionId: $valetSessionId
      facilityId: $facilityId
    ) {
      ...ConciergeValetSessionFields
    }
  }
  ${conciergeValetSessionFields}
`;

/*
 * Intake details captured on the tablet. Omitted variables are left untouched
 * server-side, and the booking comes back so Apollo patches every cached copy.
 */
export const updateBookingGuestInfo = gql`
  mutation updateBookingGuestInfo(
    $bookingId: ID!
    $guestPhone: String
    $carType: String
  ) {
    updateBookingGuestInfo(
      booking: { id: $bookingId, guestPhone: $guestPhone, carType: $carType }
    ) {
      ...ConciergeBookingFields
    }
  }
  ${conciergeBookingFields}
`;

export const flagBookingVip = gql`
  mutation flagBookingVip(
    $bookingId: ID!
    $facilityId: ID!
    $vipContactIds: [ID!]!
  ) {
    flagBookingVip(
      bookingId: $bookingId
      facilityId: $facilityId
      vipContactIds: $vipContactIds
    ) {
      ...ConciergeBookingFields
    }
  }
  ${conciergeBookingFields}
`;

export const conciergeBooking = gql`
  query conciergeBooking($bookingId: ID!) {
    booking(id: $bookingId) {
      ...ConciergeBookingFields
    }
  }
  ${conciergeBookingFields}
`;

/* ---------- Vehicle condition photos ---------- */

const vehiclePhotoFields = gql`
  fragment VehiclePhotoFields on VehiclePhoto {
    id
    angle
    stage
    contentType
    sizeBytes
    width
    height
    capturedAt
    viewUrl
  }
`;

/*
 * Presigned PUT for one angle of one stage. contentType comes back normalised
 * and MUST be the Content-Type of the PUT - S3 rejects the signature otherwise.
 */
export const vehiclePhotoUploadUrl = gql`
  mutation vehiclePhotoUploadUrl(
    $bookingId: ID!
    $facilityId: ID!
    $angle: VehiclePhotoAngleEnum!
    $stage: VehiclePhotoStageEnum!
    $contentType: String!
  ) {
    vehiclePhotoUploadUrl(
      bookingId: $bookingId
      facilityId: $facilityId
      angle: $angle
      stage: $stage
      contentType: $contentType
    ) {
      uploadUrl
      key
      angle
      stage
      contentType
      expiresInSeconds
    }
  }
`;

/* Written only once the object is in S3, so no row can outlive its object. */
export const confirmVehiclePhoto = gql`
  mutation confirmVehiclePhoto($input: ConfirmVehiclePhotoInput!) {
    confirmVehiclePhoto(input: $input) {
      ...VehiclePhotoFields
    }
  }
  ${vehiclePhotoFields}
`;

/*
 * Rehydrates the tiles when a capture screen is reopened after a reload, or by a
 * second device. One stage at a time: the intake set and the handover set are
 * separate records of the car, never merged into one grid. One session at a
 * time too, so the trip before this one does not fill the tiles in.
 *
 * viewUrl is a short-lived presigned GET, so this must not be cached.
 */
export const vehiclePhotosForBooking = gql`
  query vehiclePhotosForBooking(
    $bookingId: ID!
    $facilityId: ID!
    $stage: VehiclePhotoStageEnum!
    $valetSessionId: ID
  ) {
    vehiclePhotos(
      bookingId: $bookingId
      facilityId: $facilityId
      stage: $stage
      valetSessionId: $valetSessionId
    ) {
      ...VehiclePhotoFields
    }
  }
  ${vehiclePhotoFields}
`;

/*
 * How many photos exist at each stage of one booking, for the line in the
 * booking view's operational history. Three aliases rather than three round
 * trips, and no viewUrl: presigning a dozen GETs to render a count would spend
 * them all before anybody clicked a stage open.
 */
export const vehiclePhotoStages = gql`
  query vehiclePhotoStages($bookingId: ID!, $facilityId: ID!) {
    intake: vehiclePhotos(
      bookingId: $bookingId
      facilityId: $facilityId
      stage: INTAKE
    ) {
      id
      angle
      capturedAt
    }
    parking: vehiclePhotos(
      bookingId: $bookingId
      facilityId: $facilityId
      stage: PARKING
    ) {
      id
      angle
      capturedAt
    }
    handover: vehiclePhotos(
      bookingId: $bookingId
      facilityId: $facilityId
      stage: HANDOVER
    ) {
      id
      angle
      capturedAt
    }
  }
`;

export const consoleInventoryForecast = gql`
  query consoleInventoryForecast($lotId: ID, $startDate: String!, $endDate: String!) {
    consoleInventoryForecast(lotId: $lotId, startDate: $startDate, endDate: $endDate) {
      capacity
      timezone
      days {
        date
        arrivals
        departures
        occupancy
        soldOut
        movements {
          kind
          bookingId
          at
          plate
          guestName
          isVip
          isExpected
        }
      }
    }
  }
`;

/*
 * Month demand for the Pickup capacity card. The backend resolves real valet
 * pickup times first and falls back to booking arrival/departure estimates, so
 * the dashboard receives one timestamp-only projection for both sources.
 */
export const consoleValetCapacityForecast = gql`
  query consoleValetCapacityForecast($installationId: ID!, $month: String!) {
    consoleValetCapacityForecast(
      installationId: $installationId
      month: $month
    ) {
      pickups {
        days {
          date
          entries {
            scheduledAt
          }
        }
      }
      parking {
        days {
          date
          entries {
            scheduledAt
          }
        }
      }
    }
  }
`;

export const consoleForecastLots = gql`
  query consoleForecastLots {
    consoleForecastLots {
      id
      name
      type
      capacity
    }
  }
`;
